import { Component, computed, DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { EntityIdType } from "@front/m19-api-client";
import { singleStrategyInStrategyGroup, StrategyGroupEx } from "@front/m19-models";
import { AccountSelectionService, AuthService, SpStrategiesService, StrategyService } from "@front/m19-services";
import { IAlertComponent, ITab, ITabsComponent, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ActivityComponent, ActivityFilter } from "@m19-board/activities/activity/activity.component";
import {
  AdvertisingStatsGridComponent,
  GlobalStrategyStats,
} from "@m19-board/insights/advertising-stats/advertising-stats-grid.component";
import { EditableTextComponent } from "@m19-board/shared/editable-text/editable-text.component";
import { StrategyPageAdvancedSettingsComponent } from "@m19-board/strategies/strategy-page/strategy-page-advanced-settings/strategy-page-advanced-settings.component";
import {
  ICON_ADVANCED_SETTINGS,
  ICON_CAMPAIGN,
  ICON_CHART_LINE,
  ICON_CLOCK,
  ICON_PRODUCT_SECTION,
  ICON_SYNC,
} from "@m19-board/utils/iconsLabels";
import { UntilDestroy } from "@ngneat/until-destroy";
import { combineLatest, map } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { StrategyGroupProductsComponent } from "./strategy-products/strategy-group-products.component";
import { StrategyGroupStrategiesComponent } from "./strategy-strategies/strategy-group-strategies.component";
import { LoadingService } from "@m19-board/services/loading.service";
import { NavBreadcrumbDirective } from "@m19-board/containers/page-layout/nav-breadcrumb.directive";

enum SpGroupPageSection {
  PRODUCTS,
  CAMPAIGNS,
  STATS,
  ACTIVITIES,
  ADVANCED_SETTINGS,
}

@UntilDestroy()
@Component({
  templateUrl: "./sp-strategy-group-page.component.html",
  imports: [
    FormsModule,
    TranslocoDirective,
    EditableTextComponent,
    IAlertComponent,
    StrategyGroupProductsComponent,
    ITabsComponent,
    StrategyGroupStrategiesComponent,
    ActivityComponent,
    AdvertisingStatsGridComponent,
    StrategyPageAdvancedSettingsComponent,
    NavBreadcrumbDirective,
  ],
  standalone: true,
})
export class SpStrategyGroupPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly spStrategiesService = inject(SpStrategiesService);
  private readonly strategyService = inject(StrategyService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly authService = inject(AuthService);
  private readonly loadingService = inject(LoadingService);

  readonly destroyRef = inject(DestroyRef);
  readonly ICON_SYNC = ICON_SYNC;
  readonly SECTIONS = SpGroupPageSection;

  tabs = computed<ITab<SpGroupPageSection>[]>(() => [
    {
      label: this.translocoService.translate("sd-strategy-creation.advertised_products"),
      value: SpGroupPageSection.PRODUCTS,
      icon: ICON_PRODUCT_SECTION,
      badge: this.strategyGroup()?.asins?.length + "",
      testId: "products",
    },
    {
      label: this.translocoService.translate("common.strategies"),
      value: SpGroupPageSection.CAMPAIGNS,
      icon: ICON_CAMPAIGN,
      testId: "strategies",
    },
    { label: "Stats", value: SpGroupPageSection.STATS, icon: ICON_CHART_LINE, testId: "stats" },
    {
      label: this.translocoService.translate("page-title.activities"),
      value: SpGroupPageSection.ACTIVITIES,
      icon: ICON_CLOCK,
      testId: "activities",
    },
    {
      label: this.translocoService.translate("advanced-settings-modal.advanced_settings"),
      value: SpGroupPageSection.ADVANCED_SETTINGS,
      icon: ICON_ADVANCED_SETTINGS,
      hidden: !this.displayAdvancedSettingsSection(),
      testId: "adv-settings",
    },
  ]);
  readonly advancedSettings: Record<string, boolean> = {
    strategyGroup: true,
  };
  activeTab = signal(1);
  selectedSection = computed(() => this.tabs()[this.activeTab()].value);

  readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$);
  readonly user = toSignal(this.authService.loggedUser$);
  readonly isReadOnly = toSignal<boolean>(this.accountSelectionService.readOnlyMode$);

  private readonly strategyGroup$ = this.route.paramMap.pipe(
    tap(() => this.loadingService.loadingOn()),
    switchMap((params) => {
      const id = Number(params.get("id"));
      return this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
        switchMap((am) => this.spStrategiesService.getStrategyGroups(am.accountId, am.marketplace)),
        map((strategyGroups) => strategyGroups.get(id)),
      );
    }),
    tap((strategyGroup) => this.checkRedirect(strategyGroup)),
    tap(() => this.loadingService.loadingOff()),
    takeUntilDestroyed(this.destroyRef),
  );

  strategyGroup = toSignal(this.strategyGroup$);

  strategiesPerAsin = computed(() => {
    return this.strategyService.getStrategyPerAsin(this.strategyGroup()!);
  });

  strategyGroupName = computed(() => this.strategyGroup()?.strategyGroupName ?? "");

  displayAdvancedSettingsSection = computed(() => {
    return (
      (this.strategyGroup()?.blacklist?.length ?? 0) === 0 ||
      this.strategyGroup()?.brandStrategies.length === 0 ||
      this.strategyGroup()?.keywordStrategies.length === 0
    );
  });

  activityFilter = computed<ActivityFilter[]>(() => {
    return [
      { primaryType: EntityIdType.strategyGroupId, primaryId: this.strategyGroup()!.strategyGroupId },
      ...this.strategyGroup()!
        .brandStrategies.concat(this.strategyGroup()!.keywordStrategies)
        .concat(this.strategyGroup()!.productStrategies)
        .map((s) => ({ primaryType: EntityIdType.strategyId, primaryId: s.strategyId })),
    ];
  });

  syncMessage = toSignal<string | undefined>(
    combineLatest([this.accountSelectionService.singleAccountMarketplaceSelection$, this.strategyGroup$]).pipe(
      switchMap(([accountMarketplace, strategyGroup]) =>
        this.strategyService.getStrategyNextPushHours(strategyGroup!, accountMarketplace!),
      ),
      map((nbHours) => {
        if (!nbHours) return undefined;
        return this.translocoService.translate("sp-strategy-group-page.time_for_update", {
          count: nbHours,
        });
      }),
    ),
  );

  readonly hiddenColumns = ["campaignType", "strategyGroup"];
  strategyGroupStatsFilter = (stats: GlobalStrategyStats) =>
    stats.strategy?.strategyGroupId == this.strategyGroup()?.strategyGroupId;

  private checkRedirect(strategyGroup: StrategyGroupEx | undefined): void {
    if (!strategyGroup) {
      this.router.navigate(["/advertising/sponsored-product"], { queryParamsHandling: "merge" });
      return;
    }

    if (singleStrategyInStrategyGroup(strategyGroup)) {
      this.router.navigate(
        [
          "/advertising/sponsored-product/strategy-group",
          strategyGroup.strategyGroupId,
          strategyGroup.productStrategies[0].strategyId,
        ],
        { queryParamsHandling: "merge" },
      );
    }
  }

  updateStrategyName(newName: string) {
    if (!this.strategyGroup() || !this.accountMarketplace() || newName.trim() === "") return;
    this.strategyService
      .updateStrategyGroup({
        accountId: this.strategyGroup()!.accountId!,
        marketplace: this.strategyGroup()!.marketplace!,
        organizationId: this.accountMarketplace()!.resourceOrganizationId!,
        strategyGroupId: this.strategyGroup()!.strategyGroupId!,
        strategyGroupName: newName,
      })
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("sp-strategy-group-page.strategy_group_name_updated"),
          );
        },
        error: (e: string) => {
          this.toastrService.error(
            e,
            this.translocoService.translate("sp-strategy-group-page.strategy_group_name_update_eror"),
          );
        },
      });
  }
}
